import { useEffect, useState, useCallback } from "react";
import { Modal, Button } from "react-bootstrap";
import { Input, InputNumber, Table } from "antd";
import Swal from "sweetalert2";
import { ControllerListarSegurosClinica } from "../../../Service/Services.seguros";
import { ListCategoriaExamen } from "../../../Service/Service.categoriaExamen";
import { ListarProcedimientoConPrecios } from "../../../Service/Service.examen";
import SelectSearchable from "../../SelectSeachble/SelectSearchble";
import { GetTokenDecoded } from "../../../utils/storeUsuario";
import { calcularMontosCobertura } from "../../../utils/copagoCalculo";

export default function BuscarProcedimientoEditarModal({ show, handleClose, onAgregar, yaAgregados = [] }) {
    const [seguros, setSeguros] = useState([]);
    const [tipoExamen, setTipoExamen] = useState([]);
    const [selectedSeguro, setSelectedSeguro] = useState(null);
    const [selectedTipo, setSelectedTipo] = useState(null);
    const [procedimientos, setProcedimientos] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);

    const [copagoGlobal, setCopagoGlobal] = useState(null);
    const [aplicarGlobal, setAplicarGlobal] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedProcedimientos, setSelectedProcedimientos] = useState([]);

    const fetchFiltros = useCallback(async () => {
        const idclinica = GetTokenDecoded().idclinica;

        const segurosData = await ControllerListarSegurosClinica(idclinica);
        const segurosFiltrados = segurosData.filter(seg => seg.tipoSeguro.tipo === "SEGUROS");
        const segurosOptions = segurosFiltrados.map(s => ({ value: s.idseguro, label: s.nombre }));
        setSeguros(segurosOptions);
        setSelectedSeguro(segurosOptions[0]);

        const tipoData = await ListCategoriaExamen();
        const tipoOptions = tipoData.data.map(t => ({ value: t.IdCategoriaExamen, label: t.Descripcion }));
        setTipoExamen(tipoOptions);
        setSelectedTipo(tipoOptions[0]);
    }, []);

    // TARIFARIOS-PERF-02: aseguradora Y categoria las filtra el SERVIDOR
    // (antes se bajaba el tarifario entero, 9.590 filas / 4,32 MB, para
    // quedarse con ~107). La tercera condicion -- excluir los que el plan YA
    // tiene -- se queda aqui a proposito: depende de lo que el usuario tiene
    // en pantalla, no de nada que el servidor pueda saber.
    const fetchProcedimientos = useCallback(async () => {
        if (!selectedSeguro || !selectedTipo) return;
        setLoading(true);
        const all = await ListarProcedimientoConPrecios({
            seguro_id: selectedSeguro.value,
            categoria_id: selectedTipo.value,
        });
        const filtrados = (all?.data || [])
            .filter(p =>
                !yaAgregados.some(ag => ag.idprocedimiento_company_precios === p.idprocedimiento_company_precios)
            )
            .map(p => ({
                ...p,
                id_tipo_examen: p.ExamenTipo?.idExamenTipo
            }));

        setProcedimientos(filtrados);
        setLoading(false);
    }, [selectedSeguro, selectedTipo, yaAgregados]);

    useEffect(() => {
        if (show) fetchFiltros();
    }, [show]);

    useEffect(() => {
        if (selectedSeguro && selectedTipo) fetchProcedimientos();
    }, [selectedSeguro, selectedTipo, fetchProcedimientos]);

    useEffect(() => {
        if (copagoGlobal !== null && aplicarGlobal) {
            const copago = parseFloat(copagoGlobal);
            setSelectedProcedimientos(prev =>
                prev.map(p => {
                    const m = calcularMontosCobertura(p.precio, copago);
                    return {
                        ...p,
                        copago: m.copago,
                        precio_paciente: m.precioPaciente,
                        precio_empresa: m.precioEmpresa
                    };
                })
            );
        }
    }, [copagoGlobal, aplicarGlobal]);

    const onChangeSeleccion = (keys, rows) => {
        setSelectedRowKeys(keys);
        const nuevos = rows.map(r => {
            return {
                ...r,
                id_tipo_examen: r.ExamenTipo?.idExamenTipo ?? null,
                copago: 0,
                precio_paciente: 0,
                precio_empresa: r.precio  // copago 0 -> la aseguradora cubre el 100%
            };
        });
        setSelectedProcedimientos(nuevos);
    };

    const columnas = [
        {
            title: 'Código',
            dataIndex: ['ExamenTipo', 'codigo']
        },
        {
            title: 'Descripción',
            dataIndex: ['ExamenTipo', 'descripcion']
        },
        {
            title: 'Precio',
            dataIndex: 'precio',
            render: (val) => `$${val.toFixed(2)}`
        },
        {
            title: '% Copago',
            dataIndex: 'copago',
            render: (_, record) => (
                <InputNumber
                    min={0}
                    max={100}
                    value={record.copago}
                    onChange={(val) => {
                        const copago = parseFloat(val ?? 0);
                        setSelectedProcedimientos(prev =>
                            prev.map(p =>
                                p.idprocedimiento_company_precios === record.idprocedimiento_company_precios
                                    ? {
                                        ...p,
                                        copago,
                                        precio_paciente: calcularMontosCobertura(p.precio, copago).precioPaciente,
                                        precio_empresa: calcularMontosCobertura(p.precio, copago).precioEmpresa
                                    }
                                    : p
                            )
                        );
                    }}
                />
            )
        },
        {
            title: 'P. Paciente',
            dataIndex: 'precio_paciente',
            render: (val) => `$${val?.toFixed(2) || '0.00'}`
        },
        {
            title: 'P. Empresa',
            dataIndex: 'precio_empresa',
            render: (val) => `$${val?.toFixed(2) || '0.00'}`
        }
    ];

    const handleAgregar = () => {
        if (!selectedProcedimientos.length) {
            Swal.fire("Atención", "Selecciona al menos un procedimiento", "info");
            return;
        }
        onAgregar(selectedProcedimientos);
        handleClose();
    };

    return (
        <Modal show={show} onHide={handleClose} size="xl" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Buscar Procedimientos</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="d-flex gap-2 mb-2">
                    <Input
                        placeholder="Buscar por código o descripción"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <SelectSearchable
                        listCompany={seguros}
                        handleChange={(op) => setSelectedSeguro(op)}
                        selectedOption={selectedSeguro}
                    />
                    <SelectSearchable
                        listCompany={tipoExamen}
                        handleChange={(op) => setSelectedTipo(op)}
                        selectedOption={selectedTipo}
                    />
                </div>

                <div className="d-flex align-items-center mb-2">
                    <InputNumber
                        min={0}
                        max={100}
                        value={copagoGlobal}
                        onChange={(val) => setCopagoGlobal(val)}
                        placeholder="% Copago general"
                        style={{ marginRight: '10px', width: '120px' }}
                    />
                    <label className="form-check-label d-flex align-items-center">
                        <input
                            type="checkbox"
                            className="form-check-input me-1"
                            checked={aplicarGlobal}
                            onChange={(e) => setAplicarGlobal(e.target.checked)}
                        />
                        Aplicar a todos los seleccionados
                    </label>
                </div>

                <Table
                    rowKey="idprocedimiento_company_precios"
                    dataSource={selectedProcedimientos.filter(p =>
                        p.ExamenTipo.codigo.toLowerCase().includes(search.toLowerCase()) ||
                        p.ExamenTipo.descripcion.toLowerCase().includes(search.toLowerCase())
                      )}
                      
                    columns={columnas}
                    rowSelection={{
                        selectedRowKeys,
                        onChange: onChangeSeleccion,
                        preserveSelectedRowKeys: true
                    }}
                    loading={loading}
                    size="small"
                    scroll={{ x: 'max-content', y: 300 }}
                />
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Cancelar
                </Button>
                <Button variant="primary" onClick={handleAgregar}>
                    Agregar seleccionados
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
